import React, { useState } from "react";
import { useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import { getUserGroups } from "../Authentication/authSlice";
import { useGetLSPSStateQuery, useUpdateLSPSStateMutation } from "./operatorDashboardAPISlice";
import LSPSConfirmDialog from "./LSPSConfirmDialog";
import LSPSStatusChip from "./LSPSStatusChip";
import { LSPS, confirmCopy } from "./lspsSwitch";

const CAN_SWITCH = ["dsm_operator", "global_super_user"];

/**
 * The master switch for one Settings tab (Load Shifting or Peak Shaving), beside its title
 * (NEU-697), behind the type-CONFIRM dialog (NEU-1225). The switch shows the saved state:
 * flipping it only opens the dialog, and it moves once the server has saved the change.
 */
export default function LSPSMasterSwitch({ kind }) {
  const { name, field, activeKey } = LSPS[kind];
  const userGroups = useSelector(getUserGroups) || [];
  const canSwitch = CAN_SWITCH.some((group) => userGroups.includes(group));

  const { data: state } = useGetLSPSStateQuery(undefined, { pollingInterval: 60000 });
  const [updateLSPSState, { isLoading: saving }] = useUpdateLSPSStateMutation();
  const [pending, setPending] = useState(null); // the value asked for, while the dialog is open
  const [toast, setToast] = useState(null); // {level, text}

  if (!state) return null;
  const on = !!state[field];

  const confirm = async () => {
    const turnOn = pending;
    try {
      await updateLSPSState({ body: { [field]: turnOn } }).unwrap();
      setToast({ level: "success", text: confirmCopy(name, turnOn).done });
    } catch (err) {
      setToast({
        level: "error",
        text: err?.data?.error_message || err?.data?.message || `Couldn't change ${name}. It is unchanged; try again.`,
      });
    }
    setPending(null);
  };

  const control = (
    <Switch
      checked={on}
      disabled={!canSwitch || saving}
      onChange={(e) => setPending(e.target.checked)}
      inputProps={{ "aria-label": `${name} master switch` }}
      sx={{
        "& .MuiSwitch-switchBase.Mui-checked": { color: "#FDB940" },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#FDB940" },
      }}
    />
  );

  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
      {canSwitch ? control : (
        <Tooltip title="View-only accounts can't change this." placement="top" arrow>
          <span>{control}</span>
        </Tooltip>
      )}
      <LSPSStatusChip name={name} on={on} active={!!state[activeKey]} />

      <LSPSConfirmDialog
        open={pending !== null}
        name={name}
        turnOn={!!pending}
        busy={saving}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast?.level || "info"} variant="filled" onClose={() => setToast(null)}>
          {toast?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
